import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Box,
    Paper,
    Tabs,
    Tab,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import UserManagement from './components/UserManagement';
import QRCodeManager from './components/QRCodeManager';
import ChangePasswordForm from './components/ChangePasswordForm';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';

/**
 * TabPanel component for managing tab content
 */
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
        </div>
    );
}

/**
 * Global Settings Page Component
 * Provides centralized settings management including user management
 */
const GlobalSettings = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState(0);
    const { canBusiness } = usePermissions();

    // Check permissions
    const canSeeUserTabs = canBusiness(PERMISSIONS.USER_READ);
    const canSeeBusinessProfile = canBusiness(PERMISSIONS.BUSINESS_QR_CODE_READ);

    // Define all available tabs based on permissions
    const availableTabs = useMemo(() => {
        const tabs = [];

        // Internal Users tab - requires USER_READ
        if (canSeeUserTabs) {
            tabs.push({
                id: 'internal-users',
                label: isMobile ? 'Internal' : 'Internal Users',
                icon: <PeopleIcon fontSize={isMobile ? 'small' : 'medium'} />,
                component: <UserManagement userType="internal" />,
            });
        }

        // External Users tab - requires USER_READ
        if (canSeeUserTabs) {
            tabs.push({
                id: 'external-users',
                label: isMobile ? 'External' : 'External Users',
                icon: <BusinessIcon fontSize={isMobile ? 'small' : 'medium'} />,
                component: <UserManagement userType="external" />,
            });
        }

        // Business Profile tab - requires BUSINESS_QR_CODE_READ
        if (canSeeBusinessProfile) {
            tabs.push({
                id: 'business-profile',
                label: isMobile ? 'Profile' : 'Business Profile',
                icon: <QrCodeIcon fontSize={isMobile ? 'small' : 'medium'} />,
                component: <QRCodeManager />,
            });
        }

        // Account Settings tab - always visible (personal settings)
        tabs.push({
            id: 'account-settings',
            label: isMobile ? 'Account' : 'Account Settings',
            icon: <AccountCircleIcon fontSize={isMobile ? 'small' : 'medium'} />,
            component: (
                <Box sx={{ px: { xs: 1, sm: 3 } }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: '#0a0b0d',
                            fontSize: '1.125rem',
                        }}
                    >
                        Account Security
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Manage your account security settings.
                    </Typography>
                    <ChangePasswordForm />
                </Box>
            ),
        });

        return tabs;
    }, [canSeeUserTabs, canSeeBusinessProfile, isMobile]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleBack = () => {
        navigate('/projects');
    };

    return (
        <Box
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                minHeight: 'calc(100vh - 64px)',
            }}
        >
            {/* Responsive Grid2 Layout */}
            <Grid2 container spacing={{ xs: 2, sm: 3 }}>
                {/* Header Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: { xs: 2, sm: 3 },
                            px: { xs: 0, sm: 0.5 },
                        }}
                    >
                        <IconButton
                            onClick={handleBack}
                            sx={{
                                mr: { xs: 0.5, sm: 1 },
                                color: '#5b616e',
                                p: { xs: 0.5, sm: 0.75 },
                                '&:hover': {
                                    backgroundColor: 'rgba(17, 51, 83, 0.02)',
                                },
                            }}
                            size={isMobile ? 'small' : 'medium'}
                        >
                            <ArrowBackIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>

                        <Typography
                            component="h1"
                            sx={{
                                fontWeight: 500,
                                color: '#0a0b0d',
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Settings
                        </Typography>
                    </Box>
                </Grid2>

                {/* Main Content Area */}
                <Grid2 size={{ xs: 12 }}>
                    <Paper
                        sx={{
                            borderRadius: { xs: '8px', sm: '10px' },
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                            border: '1px solid #f0f0f0',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Responsive Tabs */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                variant={isMobile ? 'scrollable' : 'standard'}
                                scrollButtons={isMobile ? 'auto' : false}
                                allowScrollButtonsMobile
                                sx={{
                                    px: { xs: 1, sm: 3 },
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        fontWeight: 500,
                                        color: '#5b616e',
                                        py: { xs: 1.5, sm: 2 },
                                        minHeight: { xs: 42, sm: 48 },
                                        minWidth: { xs: 'auto', sm: 'auto' },
                                        px: { xs: 1, sm: 2 },
                                        '&.Mui-selected': {
                                            color: '#1652f0',
                                        },
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: '#1652f0',
                                    },
                                    '& .MuiTabs-scrollButtons': {
                                        '&.Mui-disabled': {
                                            opacity: 0.3,
                                        },
                                    },
                                }}
                            >
                                {availableTabs.map((tab) => (
                                    <Tab
                                        key={tab.id}
                                        icon={tab.icon}
                                        iconPosition="start"
                                        label={tab.label}
                                        sx={{
                                            '& .MuiTab-iconWrapper': {
                                                mr: { xs: 0.5, sm: 1 },
                                            },
                                        }}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        {/* Tab Content */}
                        {availableTabs.map((tab, index) => (
                            <TabPanel key={tab.id} value={activeTab} index={index}>
                                {tab.component}
                            </TabPanel>
                        ))}
                    </Paper>
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default GlobalSettings;
